import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Plus, Save, FileCheck2, X } from 'lucide-react';
import { API, authHeaders, apiErr } from './adminApi';

const usd = (n) => `$${(Number(n) || 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
const todayISO = () => new Date().toISOString().slice(0, 10);

const EMPTY_LABOR = { label: '', qty: 1, unit_price: 0, category: 'labor' };
const EMPTY_PARTS = { label: '', qty: 1, unit_price: 0, category: 'parts' };

const STARTING_FORM = {
  customer_name: '',
  customer_phone: '',
  customer_email: '',
  customer_address: '',
  firearm: { make: '', model: '', caliber: '', type: '', serial: '', color: '', accessories: '' },
  service_description: '',
  labor_items: [{ ...EMPTY_LABOR }],
  parts_items: [{ ...EMPTY_PARTS }],
  tax_rate: 0,
  due_date: '',
  gunsmith_notes: '',
  notes: '',
  deposit_required: 0,
  deposit_paid: 0,
  deposit_paid_date: '',
  full_payment_due_on_completion: true,
  letterhead_id: '',
};

/**
 * Structured invoice form — clone of the structured estimate form. Same Navy/Orange
 * paper-template layout, adapted with invoice-specific fields (due date, notes).
 * Payments added after invoice creation flow through the existing PaymentDialog.
 */
const StructuredInvoiceForm = ({ open, onClose, initial, letterheads, onSaved }) => {
  const [form, setForm] = useState(STARTING_FORM);
  const [saving, setSaving] = useState(false);
  const isEdit = Boolean(initial?.id);

  useEffect(() => {
    if (!open) return;
    if (initial?.id) {
      const items = initial.line_items || [];
      const labor = items.filter((i) => (i.category || 'parts') === 'labor');
      const parts = items.filter((i) => (i.category || 'parts') !== 'labor');
      setForm({
        ...STARTING_FORM,
        ...initial,
        firearm: { ...STARTING_FORM.firearm, ...(initial.firearm || {}) },
        labor_items: labor.length ? labor.map((x) => ({ ...x, category: 'labor' })) : [{ ...EMPTY_LABOR }],
        parts_items: parts.length ? parts.map((x) => ({ ...x, category: 'parts' })) : [{ ...EMPTY_PARTS }],
        customer_email: initial.customer_email || '',
        customer_phone: initial.customer_phone || '',
        customer_address: initial.customer_address || '',
        service_description: initial.service_description || '',
        gunsmith_notes: initial.gunsmith_notes || '',
        notes: initial.notes || '',
        due_date: initial.due_date || '',
        deposit_required: initial.deposit_required || 0,
        deposit_paid: initial.deposit_paid || 0,
        deposit_paid_date: initial.deposit_paid_date || '',
        full_payment_due_on_completion: initial.full_payment_due_on_completion !== false,
        letterhead_id: initial.letterhead_id || '',
        tax_rate: initial.tax_rate || 0,
      });
    } else {
      setForm({
        ...STARTING_FORM,
        deposit_paid_date: '',
        due_date: '',
      });
    }
  }, [open, initial]);

  const totals = useMemo(() => {
    const sumLines = (rows) => rows.reduce((s, r) => s + (Number(r.qty) || 0) * (Number(r.unit_price) || 0), 0);
    const labor = sumLines(form.labor_items);
    const parts = sumLines(form.parts_items);
    const subtotal = labor + parts;
    const tax = subtotal * ((Number(form.tax_rate) || 0) / 100);
    const total = subtotal + tax;
    const balance = Math.max(0, total - (Number(form.deposit_paid) || 0));
    return { labor, parts, subtotal, tax, total, balance };
  }, [form.labor_items, form.parts_items, form.tax_rate, form.deposit_paid]);

  const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setNum = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value === '' ? '' : Number(e.target.value) }));
  const setFirearm = (k) => (e) => setForm((f) => ({ ...f, firearm: { ...f.firearm, [k]: e.target.value } }));

  const updateRow = (kind, idx, patch) => {
    setForm((f) => {
      const next = [...f[kind]];
      next[idx] = { ...next[idx], ...patch };
      return { ...f, [kind]: next };
    });
  };
  const addRow = (kind) => {
    const tpl = kind === 'labor_items' ? EMPTY_LABOR : EMPTY_PARTS;
    setForm((f) => ({ ...f, [kind]: [...f[kind], { ...tpl }] }));
  };
  const removeRow = (kind, idx) => {
    setForm((f) => {
      const next = f[kind].filter((_, i) => i !== idx);
      const tpl = kind === 'labor_items' ? EMPTY_LABOR : EMPTY_PARTS;
      return { ...f, [kind]: next.length ? next : [{ ...tpl }] };
    });
  };

  const submit = async () => {
    if (!form.customer_name.trim()) {
      toast.error('Customer name is required');
      return;
    }
    setSaving(true);
    try {
      const merged = [
        ...form.labor_items.filter((r) => (r.label || '').trim()),
        ...form.parts_items.filter((r) => (r.label || '').trim()),
      ];
      const payload = {
        layout: 'structured',
        customer_name: form.customer_name.trim(),
        customer_email: form.customer_email.trim() || null,
        customer_phone: form.customer_phone.trim(),
        customer_address: form.customer_address.trim(),
        firearm: form.firearm,
        service_description: form.service_description.trim(),
        line_items: merged,
        tax_rate: Number(form.tax_rate) || 0,
        gunsmith_notes: form.gunsmith_notes.trim(),
        notes: form.notes.trim(),
        due_date: form.due_date || null,
        deposit_required: Number(form.deposit_required) || 0,
        deposit_paid: Number(form.deposit_paid) || 0,
        deposit_paid_date: form.deposit_paid_date || null,
        full_payment_due_on_completion: Boolean(form.full_payment_due_on_completion),
        letterhead_id: form.letterhead_id || null,
      };
      let url = `${API}/api/invoices`;
      let method = 'post';
      if (isEdit) {
        url = `${API}/api/invoices/${initial.id}`;
        method = 'patch';
      }
      await axios({ url, method, headers: authHeaders(), data: payload });
      toast.success(isEdit ? 'Invoice updated' : `Invoice created · ${totals.total > 0 ? usd(totals.total) : 'draft'}`);
      onSaved?.();
      onClose();
    } catch (err) {
      toast.error(`Save failed: ${apiErr(err)}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-5xl max-h-[95vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle data-testid="structured-invoice-form-title" className="flex items-center gap-2">
            <FileCheck2 className="w-5 h-5 text-green-400" />
            {isEdit ? `Edit invoice ${initial?.invoice_number || ''}` : 'New invoice (structured)'}
          </DialogTitle>
          <DialogDescription className="text-gray-400">
            Same Navy &amp; Orange layout as the estimate. Record payments later from the invoice list to auto-log income.
          </DialogDescription>
        </DialogHeader>

        {/* Customer */}
        <Section title="Customer information">
          <Grid2>
            <Field label="Name *">
              <Input data-testid="inv-form-customer-name" value={form.customer_name} onChange={setField('customer_name')} className="bg-slate-800 border-slate-700 text-white" required />
            </Field>
            <Field label="Phone">
              <Input data-testid="inv-form-customer-phone" value={form.customer_phone} onChange={setField('customer_phone')} className="bg-slate-800 border-slate-700 text-white" />
            </Field>
            <Field label="Email">
              <Input data-testid="inv-form-customer-email" type="email" value={form.customer_email} onChange={setField('customer_email')} className="bg-slate-800 border-slate-700 text-white" />
            </Field>
            <Field label="Address">
              <Input data-testid="inv-form-customer-address" value={form.customer_address} onChange={setField('customer_address')} className="bg-slate-800 border-slate-700 text-white" />
            </Field>
          </Grid2>
        </Section>

        {/* Firearm */}
        <Section title="Firearm information">
          <Grid3>
            <Field label="Make"><Input data-testid="inv-form-firearm-make" value={form.firearm.make} onChange={setFirearm('make')} className="bg-slate-800 border-slate-700 text-white" /></Field>
            <Field label="Model"><Input data-testid="inv-form-firearm-model" value={form.firearm.model} onChange={setFirearm('model')} className="bg-slate-800 border-slate-700 text-white" /></Field>
            <Field label="Caliber"><Input data-testid="inv-form-firearm-caliber" value={form.firearm.caliber} onChange={setFirearm('caliber')} className="bg-slate-800 border-slate-700 text-white" /></Field>
            <Field label="Type"><Input data-testid="inv-form-firearm-type" value={form.firearm.type} onChange={setFirearm('type')} placeholder="Pistol / Rifle / Shotgun" className="bg-slate-800 border-slate-700 text-white" /></Field>
            <Field label="Serial #"><Input data-testid="inv-form-firearm-serial" value={form.firearm.serial} onChange={setFirearm('serial')} className="bg-slate-800 border-slate-700 text-white font-mono" /></Field>
            <Field label="Color / Finish"><Input data-testid="inv-form-firearm-color" value={form.firearm.color} onChange={setFirearm('color')} className="bg-slate-800 border-slate-700 text-white" /></Field>
          </Grid3>
          <Field label="Other / Optics / Accessories" className="mt-3">
            <Input data-testid="inv-form-firearm-accessories" value={form.firearm.accessories} onChange={setFirearm('accessories')} placeholder="Vortex Strike Eagle, sling, suppressor mount…" className="bg-slate-800 border-slate-700 text-white" />
          </Field>
        </Section>

        {/* Service description */}
        <Section title="Service performed / description of work">
          <Textarea
            data-testid="inv-form-service-description"
            value={form.service_description}
            onChange={setField('service_description')}
            rows={3}
            placeholder="Installed Apex trigger; cerakoted slide in olive drab; replaced recoil spring assembly…"
            className="bg-slate-800 border-slate-700 text-white"
          />
        </Section>

        {/* Labor + Parts tables */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-2">
          <ItemsTable
            title="Labor"
            testidPrefix="inv-form-labor"
            rows={form.labor_items}
            onUpdate={(idx, patch) => updateRow('labor_items', idx, patch)}
            onAdd={() => addRow('labor_items')}
            onRemove={(idx) => removeRow('labor_items', idx)}
            sectionTotal={totals.labor}
          />
          <ItemsTable
            title="Parts / Materials"
            testidPrefix="inv-form-parts"
            rows={form.parts_items}
            onUpdate={(idx, patch) => updateRow('parts_items', idx, patch)}
            onAdd={() => addRow('parts_items')}
            onRemove={(idx) => removeRow('parts_items', idx)}
            sectionTotal={totals.parts}
          />
        </div>

        {/* Totals */}
        <Section title="Totals">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Tax rate (%)">
              <Input data-testid="inv-form-tax-rate" type="number" min="0" step="0.001" value={form.tax_rate} onChange={setNum('tax_rate')} className="bg-slate-800 border-slate-700 text-white" />
            </Field>
            <div className="bg-slate-950/40 border border-slate-800 rounded p-3 text-sm space-y-1 font-mono">
              <Row label="Labor total" value={usd(totals.labor)} />
              <Row label="Parts total" value={usd(totals.parts)} />
              <Row label="Subtotal" value={usd(totals.subtotal)} />
              <Row label={`Tax (${form.tax_rate || 0}%)`} value={usd(totals.tax)} />
              <Row label="Invoice total" value={usd(totals.total)} bold testid="inv-form-total" />
            </div>
          </div>
        </Section>

        {/* Due date + notes */}
        <Section title="Due date & notes">
          <Field label="Due date">
            <Input data-testid="inv-form-due-date" type="date" value={form.due_date || ''} onChange={setField('due_date')} className="bg-slate-800 border-slate-700 text-white" />
          </Field>
          <Field label="Internal gunsmith notes" className="mt-3">
            <Textarea
              data-testid="inv-form-gunsmith-notes"
              value={form.gunsmith_notes}
              onChange={setField('gunsmith_notes')}
              rows={2}
              placeholder="Internal-only notes for the build…"
              className="bg-slate-800 border-slate-700 text-white"
            />
          </Field>
          <Field label="Terms & customer-facing notes" className="mt-3">
            <Textarea
              data-testid="inv-form-notes"
              value={form.notes}
              onChange={setField('notes')}
              rows={2}
              placeholder="Customer-facing notes / terms (shown on invoice)…"
              className="bg-slate-800 border-slate-700 text-white"
            />
          </Field>
        </Section>

        {/* Deposit */}
        <Section title="Deposit (from estimate, if any)">
          <Grid3>
            <Field label="Deposit required">
              <Input data-testid="inv-form-deposit-required" type="number" min="0" step="0.01" value={form.deposit_required} onChange={setNum('deposit_required')} className="bg-slate-800 border-slate-700 text-white tabular-nums" />
            </Field>
            <Field label="Deposit paid">
              <Input data-testid="inv-form-deposit-paid" type="number" min="0" step="0.01" value={form.deposit_paid} onChange={setNum('deposit_paid')} className="bg-slate-800 border-slate-700 text-white tabular-nums" />
            </Field>
            <Field label="Date paid">
              <Input data-testid="inv-form-deposit-date" type="date" value={form.deposit_paid_date || ''} onChange={setField('deposit_paid_date')} className="bg-slate-800 border-slate-700 text-white" />
            </Field>
          </Grid3>
          {Number(form.deposit_paid) > 0 && (
            <p className="text-xs text-amber-300 mt-2 bg-amber-950/30 border border-amber-900/40 rounded px-3 py-2">
              💡 The deposit is treated as paid-to-date. Balance after deposit: <span className="font-bold">{usd(totals.balance)}</span>. Record any additional payments later from the invoice list.
            </p>
          )}
        </Section>

        {/* Letterhead */}
        {letterheads && letterheads.length > 0 && (
          <Section title="Letterhead override (optional)">
            <select
              data-testid="inv-form-letterhead"
              value={form.letterhead_id || ''}
              onChange={setField('letterhead_id')}
              className="w-full bg-slate-800 border border-slate-700 text-white rounded px-3 py-2 text-sm"
            >
              <option value="">Default branded</option>
              {letterheads.map((lh) => (
                <option key={lh.id} value={lh.id}>{lh.name}</option>
              ))}
            </select>
          </Section>
        )}

        <DialogFooter className="border-t border-slate-800 pt-3 mt-3">
          <Button variant="outline" onClick={onClose} disabled={saving} className="border-slate-700 text-gray-300 hover:bg-slate-800">
            Cancel
          </Button>
          <Button data-testid="inv-form-save" onClick={submit} disabled={saving} className="bg-green-600 hover:bg-green-700 text-white">
            <Save className="w-3 h-3 mr-2" />
            {saving ? 'Saving…' : (isEdit ? 'Save changes' : 'Create invoice')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const Section = ({ title, children }) => (
  <div className="border border-slate-800 bg-slate-950/30 rounded-md p-4 mt-3">
    <h3 className="text-xs uppercase tracking-widest text-amber-400 font-bold mb-3">{title}</h3>
    {children}
  </div>
);
const Grid2 = ({ children }) => <div className="grid grid-cols-1 md:grid-cols-2 gap-3">{children}</div>;
const Grid3 = ({ children }) => <div className="grid grid-cols-1 md:grid-cols-3 gap-3">{children}</div>;
const Field = ({ label, children, className = '' }) => (
  <div className={className}>
    <Label className="text-gray-300 text-xs">{label}</Label>
    {children}
  </div>
);
const Row = ({ label, value, bold, testid }) => (
  <div className={`flex items-center justify-between ${bold ? 'border-t border-slate-700 pt-1 mt-1 text-white text-base' : 'text-gray-400'}`} data-testid={testid}>
    <span>{label}</span><span className={bold ? 'font-bold text-amber-300' : ''}>{value}</span>
  </div>
);

const ItemsTable = ({ title, testidPrefix, rows, onUpdate, onAdd, onRemove, sectionTotal }) => (
  <div className="border border-slate-800 bg-slate-950/30 rounded-md p-3">
    <div className="flex items-center justify-between mb-2">
      <h3 className="text-xs uppercase tracking-widest text-amber-400 font-bold">{title}</h3>
      <span className="text-xs text-gray-400 font-mono">{usd(sectionTotal)}</span>
    </div>
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead className="text-gray-500">
          <tr>
            <th className="text-left font-medium pb-1">Description</th>
            <th className="text-right font-medium pb-1 w-12">Qty</th>
            <th className="text-right font-medium pb-1 w-20">Unit</th>
            <th className="text-right font-medium pb-1 w-20">Total</th>
            <th className="w-6 pb-1" />
          </tr>
        </thead>
        <tbody>
          {rows.map((row, idx) => {
            const lineTotal = (Number(row.qty) || 0) * (Number(row.unit_price) || 0);
            return (
              <tr key={`${testidPrefix}-${idx}`}>
                <td className="pr-1 py-0.5">
                  <Input
                    data-testid={`${testidPrefix}-label-${idx}`}
                    value={row.label}
                    onChange={(e) => onUpdate(idx, { label: e.target.value })}
                    placeholder="Description"
                    className="bg-slate-800 border-slate-700 text-white h-7 text-xs"
                  />
                </td>
                <td className="pr-1 py-0.5">
                  <Input
                    data-testid={`${testidPrefix}-qty-${idx}`}
                    type="number" step="0.5" min="0" value={row.qty}
                    onChange={(e) => onUpdate(idx, { qty: e.target.value === '' ? '' : Number(e.target.value) })}
                    className="bg-slate-800 border-slate-700 text-white h-7 text-xs text-right tabular-nums"
                  />
                </td>
                <td className="pr-1 py-0.5">
                  <Input
                    data-testid={`${testidPrefix}-price-${idx}`}
                    type="number" step="0.01" min="0" value={row.unit_price}
                    onChange={(e) => onUpdate(idx, { unit_price: e.target.value === '' ? '' : Number(e.target.value) })}
                    className="bg-slate-800 border-slate-700 text-white h-7 text-xs text-right tabular-nums"
                  />
                </td>
                <td className="text-right text-gray-300 tabular-nums px-1 whitespace-nowrap">{usd(lineTotal)}</td>
                <td className="text-center">
                  <button
                    type="button"
                    data-testid={`${testidPrefix}-remove-${idx}`}
                    onClick={() => onRemove(idx)}
                    aria-label="Remove row"
                    className="text-gray-500 hover:text-red-400 transition p-1"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
    <Button
      type="button"
      size="sm" variant="outline"
      data-testid={`${testidPrefix}-add`}
      onClick={onAdd}
      className="mt-2 w-full border-dashed border-slate-700 text-gray-400 hover:bg-slate-800 hover:text-white h-7 text-xs"
    >
      <Plus className="w-3 h-3 mr-1" /> Add row
    </Button>
  </div>
);

export default StructuredInvoiceForm;
